import {
    AccountCustomTransactionCodeDto,
    AnalysisTransactionTypeDto,
    CustomTransactionCodeDto,
} from '@treasury/api/bo';
import { DomainEntity } from '@treasury/domain/shared';
import { BillingAccountEntity } from './billingAccount.entity';

export class TransactionTypeEntity extends DomainEntity<AnalysisTransactionTypeDto> {
    public static createDefault(): TransactionTypeEntity {
        return new TransactionTypeEntity({
            analysisTransactionTypeId: 0,
            transactionName: '',
            description: '',
            sortOrder: 0,
            analysisTransactionTypeCategory: {
                analysisTransactionTypeCategoryId: 0,
                categoryName: '',
                sortOrder: 0,
            },
        });
    }

    public customTransactionCode?: CustomTransactionCodeDto;

    public createDefault(): this {
        return new TransactionTypeEntity({
            analysisTransactionTypeId: 0,
            transactionName: '',
            description: '',
            sortOrder: 0,
            analysisTransactionTypeCategory: {
                analysisTransactionTypeCategoryId: 0,
                categoryName: '',
                sortOrder: 0,
            },
        }) as this;
    }

    public get analysisTransactionTypeId(): number {
        return this.dto.analysisTransactionTypeId ?? 0;
    }

    public get transactionName() {
        return this.dto.transactionName;
    }

    public get description() {
        return this.dto.description;
    }

    public get analysisTransactionTypeCategory() {
        return this.dto.analysisTransactionTypeCategory;
    }

    public get categoryName() {
        return this.analysisTransactionTypeCategory?.categoryName || '';
    }

    public get analysisTransactionTypeCategoryId(): number {
        return this.analysisTransactionTypeCategory?.analysisTransactionTypeCategoryId || 0;
    }

    public get analysisCounter() {
        return this.customTransactionCode?.transactionCode || '';
    }

    public get customTransactionCodeId(): number {
        return this.customTransactionCode?.id || 0;
    }

    public get accountCustomTransactionCodes(): AccountCustomTransactionCodeDto[] {
        return this.customTransactionCode?.accountCustomTransactionCodes || [];
    }

    public get activeLinkedAccounts(): AccountCustomTransactionCodeDto[] {
        return (
            this.customTransactionCode?.accountCustomTransactionCodes?.filter(
                code => code.active
            ) || []
        );
    }

    public get transactionCode(): string {
        return this.customTransactionCode?.transactionCode || '';
    }

    public get linkedAccountSummary() {
        if (this.activeLinkedAccounts.length === 1) {
            const [displayAccount] = this.activeLinkedAccounts;
            return displayAccount.accountNumber;
        }
        return this.activeLinkedAccounts.length
            ? `${this.activeLinkedAccounts.length} accounts`
            : '';
    }

    public setDefaultCustomTransactionCodes() {
        this.customTransactionCode = {
            id: 0,
            transactionCode: '0',
            analysisTransactionTypeId: 0,
            active: false,
            accountCustomTransactionCodes: [],
            analysisTransactionTypeCategoryId: 0,
        };
    }

    /**
     * Generates a list of custom transaction codes within a transaction type.
     * Each custom transaction code includes a list of accounts that includes both previously linked accounts and accounts to be updated.
     *
     * @param accountsToUpdate - The accounts to update
     * @param previouslyLinkedAccounts - The accounts that were previously linked
     * @param link - Whether to link or unlink the accounts
     * @returns The updated custom transaction code or undefined if no accounts were updated for this code
     */
    public getAccountsForLinking(
        accountsToUpdate: BillingAccountEntity[],
        previouslyLinkedAccounts: BillingAccountEntity[],
        link: boolean,
        customTransactionCodeId: number
    ): CustomTransactionCodeDto | undefined {
        const accountsToSend: AccountCustomTransactionCodeDto[] = [];
        const legacyLinkedAccounts = previouslyLinkedAccounts.filter(
            account => !accountsToUpdate.includes(account)
        );
        const uniqueAccountIds = new Set<number>();
        accountsToUpdate.forEach(account => {
            if (uniqueAccountIds.has(account.accountId)) {
                return; // Skip duplicate accountId
            }
            uniqueAccountIds.add(account.accountId);
            accountsToSend.push({
                id: account.id || 0,
                accountId: account.accountId,
                active: link,
                customTransactionCodeId: customTransactionCodeId,
            });
        });
        legacyLinkedAccounts.forEach(account => {
            if (uniqueAccountIds.has(account.accountId)) {
                return; // Skip duplicate accountId
            }
            uniqueAccountIds.add(account.accountId);
            accountsToSend.push({
                id: account.id,
                accountId: account.accountId,
                active: account.active,
                customTransactionCodeId: customTransactionCodeId,
            });
        });
        if (accountsToSend?.length) {
            const customCode = {
                analysisTransactionTypeId: this.analysisTransactionTypeId,
                accountCustomTransactionCodes: accountsToSend,
            } as CustomTransactionCodeDto;
            if (this.customTransactionCode?.transactionCode) {
                customCode.transactionCode = this.customTransactionCode.transactionCode;
            }
            if (this.customTransactionCode?.id) {
                customCode.id = this.customTransactionCode.id; // Ensure the ID is set if it exists
            }
            return customCode;
        }
        return undefined;
    }
}
